/**
 * ProgressBar Component
 * Visual progress indicator for comment review status.
 */
import React from 'react';
import type { Comment } from '../types';

interface ProgressBarProps {
  comments: Comment[];
}

const ProgressBar: React.FC<ProgressBarProps> = ({ comments }) => {
  const total = comments.length;
  const accepted = comments.filter((c) => c.status === 'accepted').length;
  const edited = comments.filter((c) => c.status === 'edited').length;
  const rejected = comments.filter((c) => c.status === 'rejected').length;

  const completed = accepted + edited + rejected;

  // Calculate segment widths as percentages of total
  const acceptedWidth = total > 0 ? (accepted / total) * 100 : 0;
  const editedWidth = total > 0 ? (edited / total) * 100 : 0;
  const rejectedWidth = total > 0 ? (rejected / total) * 100 : 0;

  if (total === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
        {acceptedWidth > 0 && (
          <div
            className="bg-green-500 h-full transition-all duration-300"
            style={{ width: `${acceptedWidth}%` }}
            title={`Accepted: ${accepted}`}
          />
        )}
        {editedWidth > 0 && (
          <div
            className="bg-blue-500 h-full transition-all duration-300"
            style={{ width: `${editedWidth}%` }}
            title={`Edited: ${edited}`}
          />
        )}
        {rejectedWidth > 0 && (
          <div
            className="bg-red-500 h-full transition-all duration-300"
            style={{ width: `${rejectedWidth}%` }}
            title={`Rejected: ${rejected}`}
          />
        )}
      </div>

      {/* Stats */}
      <div className="flex justify-between items-center text-xs">
        <span className="text-gray-600 font-medium">
          {completed}/{total} reviewed
        </span>
        <div className="flex items-center space-x-3">
          <span className="flex items-center text-green-600">
            <span className="w-2 h-2 rounded-full bg-green-500 mr-1" />
            {accepted}
          </span>
          <span className="flex items-center text-blue-600">
            <span className="w-2 h-2 rounded-full bg-blue-500 mr-1" />
            {edited}
          </span>
          <span className="flex items-center text-red-600">
            <span className="w-2 h-2 rounded-full bg-red-500 mr-1" />
            {rejected}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;
